import { createTheme, Theme } from "@mui/material/styles";
import { brand, rawPalette, ThemeMode } from "./palette";

const INTER = [
  "Inter",
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "sans-serif",
].join(",");

/**
 * Build a fully-themed MUI theme for the given mode using concrete hex values
 * from the shared palette. Mounted app-wide by ThemeModeProvider so every MUI
 * surface (Paper, Card, Button, Tooltipâ€¦) responds to light/dark.
 */
export const createAppTheme = (mode: ThemeMode): Theme => {
  const p = rawPalette[mode];

  const theme = createTheme({
    palette: {
      mode,
      background: { default: p.canvas, paper: p.surface },
      primary: { main: brand.primary, light: brand.electric, dark: brand.primaryDark },
      secondary: { main: brand.nektia },
      success: { main: brand.success, dark: "#15803D", light: "#4ADE80" },
      warning: { main: brand.warning, dark: "#B45309", light: "#FBBF24" },
      error: { main: brand.error, dark: "#B91C1C", light: "#F87171" },
      text: { primary: p.text, secondary: p.text2, disabled: p.muted },
      divider: p.border,
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: INTER,
      fontSize: 13,
      h1: { fontFamily: INTER, fontWeight: 700, color: p.text },
      h2: { fontFamily: INTER, fontWeight: 700, color: p.text, letterSpacing: "-0.02em" },
      h3: { fontFamily: INTER, fontWeight: 600, color: p.text, fontSize: 16 },
      h4: { fontFamily: INTER, fontWeight: 600, color: p.text },
      h5: { fontFamily: INTER, fontWeight: 600, color: p.text },
      h6: { fontFamily: INTER, fontWeight: 600, color: p.text },
      subtitle1: { fontFamily: INTER, fontWeight: 500, color: p.text2 },
      subtitle2: { fontFamily: INTER, fontWeight: 400, color: p.muted, fontSize: 11 },
      body1: { fontFamily: INTER, fontWeight: 400, color: p.text2 },
      body2: { fontFamily: INTER, fontWeight: 400, color: p.text2 },
      button: { fontFamily: INTER, fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: p.surface, backgroundImage: "none", color: p.text2 },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: p.surface,
            border: `1px solid ${p.border}`,
            borderRadius: 16,
            boxShadow:
              mode === "dark"
                ? "0 1px 2px rgba(0,0,0,0.4)"
                : "0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 10, textTransform: "none", fontWeight: 600 },
          containedPrimary: { backgroundColor: brand.primary },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: p.surface2, color: p.text, fontSize: 11, borderRadius: 8 },
        },
      },
    },
  });

  return theme;
};

// Backwards-compatible default export (dark) for any module importing `theme`.
const theme = createAppTheme("dark");
export default theme;
